import { useEffect, useMemo, useState } from "react";
import { ActivityFeed } from "./components/ActivityFeed";
import { CommandBar } from "./components/CommandBar";
import { KanbanBoard } from "./components/KanbanBoard";
import { Landing } from "./components/Landing";
import { ResumeCanvas } from "./components/ResumeCanvas";
import { actions } from "./store";
import { useStore } from "./useStore";
import { isWebMcpAvailable, REGISTERED_TOOL_NAMES } from "./webmcp-tools";

/** Minimal hash router: "#/app" shows the workspace, anything else the landing. */
function useHashRoute(): string {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return hash;
}

export default function App() {
  const route = useHashRoute();
  const { jobs, resume, activity } = useStore();
  const webmcp = useMemo(() => isWebMcpAvailable(), []);
  const [toolsOpen, setToolsOpen] = useState(false);

  // Landing page at the root; the workspace lives at #/app.
  if (route !== "#/app") {
    return <Landing onLaunch={() => (window.location.hash = "#/app")} />;
  }

  const scored = jobs.filter((j) => j.match);
  const avgScore =
    scored.length > 0
      ? Math.round(scored.reduce((s, j) => s + j.match!.score, 0) / scored.length)
      : null;

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border/70 bg-surface/30 px-4 py-3 backdrop-blur-sm sm:px-6">
        <a href="#/" title="Back to home" className="flex items-center gap-3">
          <div className="relative grid h-10 w-10 place-items-center rounded-xl border border-accent/25 bg-accent/10 shadow-[0_0_24px_-8px_rgba(232,177,92,0.5)]">
            {/* Lodestar mark — a four-point navigational star. */}
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 1.4l2.1 8.5 8.5 2.1-8.5 2.1L12 22.6l-2.1-8.5L1.4 12l8.5-2.1z"
                fill="#E8B15C"
              />
            </svg>
          </div>
          <div>
            <h1 className="wordmark text-[22px] leading-none text-text">
              Openings
            </h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-faint">
              Official jobs, sourced by an agent
            </p>
          </div>
        </a>

        <div className="flex items-center gap-2.5">
          <a
            href="https://github.com/Priyanka2-ui/Openings"
            target="_blank"
            rel="noreferrer noopener"
            title="View source on GitHub"
            className="hidden items-center gap-1.5 rounded-lg border border-border bg-surface-2/40 px-3 py-1.5 text-xs text-muted transition-colors hover:border-border-bright hover:text-text sm:flex"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.2.8-.5v-1.8c-3.2.7-3.9-1.5-3.9-1.5-.5-1.3-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.7 1.3 3.4 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2a11.5 11.5 0 016 0C17 4.6 18 4.9 18 4.9c.6 1.6.2 2.8.1 3.1.8.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.3c0 .3.2.6.8.5A11.5 11.5 0 0023.5 12C23.5 5.7 18.3.5 12 .5z" />
            </svg>
            GitHub
          </a>
          {avgScore !== null && (
            <div className="hidden items-center gap-2 rounded-lg border border-border px-3 py-1.5 sm:flex">
              <span className="font-mono text-[10px] uppercase tracking-wider text-faint">
                avg fit
              </span>
              <span className="font-display text-sm font-semibold text-signal">
                {avgScore}%
              </span>
            </div>
          )}

          {/* WebMCP status — the differentiator, made visible. */}
          <button
            onClick={() => setToolsOpen((v) => !v)}
            className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              webmcp
                ? "border-signal/40 bg-signal-soft/60 text-signal hover:bg-signal-soft"
                : "border-border bg-surface-2 text-muted hover:border-border-bright"
            }`}
            title="WebMCP tool status"
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${webmcp ? "bg-signal" : "bg-faint"}`}
            />
            WebMCP {webmcp ? "connected" : "standby"}
            <span className="font-mono text-[10px] opacity-70">
              {REGISTERED_TOOL_NAMES.length} tools
            </span>
          </button>
        </div>
      </header>

      {/* Tools popover */}
      {toolsOpen && (
        <div className="animate-slide-in border-b border-border/70 bg-surface/90 px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="eyebrow mr-1">Registered tools</span>
            {REGISTERED_TOOL_NAMES.map((t) => (
              <code
                key={t}
                className="rounded-md border border-border bg-ink px-2 py-1 font-mono text-[11px] text-accent"
              >
                {t}
              </code>
            ))}
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-muted">
            {webmcp
              ? "An in-browser agent can call these directly on this page."
              : "Not detected in this browser. Enable chrome://flags/#enable-webmcp-testing or open in an agent host (e.g. ChatGPT's in-app browser). The app is fully usable without it."}
          </p>
        </div>
      )}

      {/* ── Split-screen workspace ─────────────────────────────────────── */}
      <main className="grid min-h-0 flex-1 grid-cols-1 gap-4 p-4 sm:px-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        {/* Left — Career Pipeline */}
        <section className="flex min-h-0 min-w-0 flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h2 className="font-display text-sm font-medium tracking-wide">
                Career Pipeline
              </h2>
              <span className="font-mono text-[11px] text-faint">
                {jobs.length} role{jobs.length === 1 ? "" : "s"}
              </span>
            </div>
            {jobs.length > 0 && (
              <button
                onClick={() => {
                  if (confirm("Clear all roles from the board?")) actions.clearBoard();
                }}
                className="font-mono text-[10px] uppercase tracking-wider text-faint transition-colors hover:text-rose"
              >
                clear board
              </button>
            )}
          </div>
          <CommandBar />
          <KanbanBoard jobs={jobs} />
        </section>

        {/* Right — Resume + Activity */}
        <section className="grid min-h-0 min-w-0 grid-rows-[1.5fr_1fr] gap-4">
          <ResumeCanvas resume={resume} jobs={jobs} />
          <ActivityFeed entries={activity} />
        </section>
      </main>
    </div>
  );
}
